import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Pizza, CheckCircle2, XCircle, Clock, ShieldCheck } from "lucide-react";

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export default function MembershipCard() {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const timerRef = useRef(null);
  const now = useClock();

  useEffect(() => {
    api.get("/me/card").then((r) => setCard(r.data)).finally(() => setLoading(false));
  }, []);

  const startVerify = () => {
    setVerifying(true);
    setCountdown(60);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); setVerifying(false); return 60; }
        return c - 1;
      });
    }, 1000);
  };
  const stopVerify = () => { clearInterval(timerRef.current); setVerifying(false); setCountdown(60); };
  useEffect(() => () => clearInterval(timerRef.current), []);

  const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr = now.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
  const verifyUrl = card ? `${window.location.origin}/verify?code=${card.member_code}` : "";

  if (loading) return <div className="App"><Navbar /><p className="p-10">Loading card...</p></div>;

  return (
    <div className="App">
      {!verifying && <Navbar />}
      <div className="max-w-md mx-auto px-5 py-8">
        {/* Digital membership card */}
        <div data-testid="membership-card" className="card-brut-lg rounded-2xl overflow-hidden bg-white">
          {/* Big high-contrast status banner */}
          {card.active ? (
            <div data-testid="active-banner" className="live-bg text-white px-6 py-7 text-center border-b-4 border-[#17324D]/15">
              <div className="flex items-center justify-center gap-3">
                <CheckCircle2 size={40} strokeWidth={2.5} />
                <span className="font-heading font-extrabold text-3xl sm:text-4xl leading-none">ACTIVE MEMBER</span>
              </div>
              <p className="mt-1 text-lg font-semibold opacity-90">Verified • Apply member discount</p>
            </div>
          ) : (
            <div data-testid="inactive-banner" className="bg-[#D94A3D] text-white px-6 py-7 text-center border-b-4 border-[#17324D]/15">
              <div className="flex items-center justify-center gap-3">
                <XCircle size={36} strokeWidth={2.5} />
                <span className="font-heading font-extrabold text-3xl leading-none">NOT ACTIVE</span>
              </div>
              <p className="mt-1 font-semibold">No active membership — <Link to="/clubs" className="underline">join a club</Link></p>
            </div>
          )}

          <div className="p-6">
            <p className="text-xs tracking-[0.2em] uppercase text-[#17324D]/60">Member</p>
            <h1 className="font-heading font-bold text-2xl tracking-tight leading-none">{card.name}</h1>
            <p className="font-mono text-sm mt-1 text-[#17324D]/80">{card.member_code}</p>

            {/* QR code */}
            <div className="mt-5 flex justify-center">
              <div className="p-3 border border-[#17324D]/15 rounded-xl bg-white" data-testid="member-qr">
                <QRCodeSVG value={verifyUrl} size={180} level="M" />
              </div>
            </div>
            <p className="text-center text-xs text-[#17324D]/60 mt-2">Staff can scan to verify membership</p>

            {card.active_clubs?.length > 0 && (
              <div className="mt-4 space-y-1">
                {card.active_clubs.map((c, i) => (
                  <div key={i} className="flex justify-between text-sm border-t border-[#17324D]/10 pt-1">
                    <span className="font-medium">{c.name}</span>
                    <span className="text-[#17324D]/60">renews {c.current_period_end ? new Date(c.current_period_end).toLocaleDateString() : "—"}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Live verification: real-time clock proves this is the live app */}
            <div data-testid="live-clock" className="mt-5 flex items-center justify-center gap-2 bg-[#17324D] text-[#D94A3D] rounded-xl py-2 px-3">
              <span className="relative flex h-2.5 w-2.5">
                <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#D94A3D]"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D94A3D]"></span>
              </span>
              <Clock size={16} />
              <span className="font-mono text-lg tabular-nums">{timeStr}</span>
              <span className="text-xs text-[#17324D]/50">· {dateStr}</span>
            </div>
          </div>
        </div>

        {/* Big Redeem button */}
        {card.active && (
          <button data-testid="redeem-verify-btn" onClick={startVerify}
            className="w-full mt-5 py-5 bg-[#D94A3D] text-white font-heading font-bold text-2xl border border-[#17324D]/15 rounded-xl hover:bg-[#C33E32]">
            REDEEM NOW
          </button>
        )}

        <p className="text-center text-xs text-[#17324D]/60 mt-6">
          <Link to="/privacy" className="underline">Privacy</Link> · <Link to="/terms" className="underline">Terms</Link>
        </p>
      </div>

      {/* 60-second "Member Verified" full-screen overlay */}
      {verifying && (
        <div data-testid="verified-overlay" className="fixed inset-0 z-[100] live-bg flex flex-col items-center justify-center text-white px-6 text-center">
          <span className="bg-white text-[#D94A3D] p-5 rounded-full border-4 border-white spin-slow mb-6">
            <Pizza size={64} strokeWidth={2.5} />
          </span>
          <ShieldCheck size={48} className="mb-2" />
          <h1 className="font-heading font-extrabold text-4xl leading-none">MEMBER VERIFIED</h1>
          <p className="text-2xl font-serif-ed italic mt-3">{card.name}</p>
          <p className="mt-2 text-lg opacity-90">{card.active_clubs?.map((c) => c.name).join(" · ")}</p>

          <div className="mt-8 flex items-center gap-2 text-lg">
            <Clock size={18} />
            <span className="font-mono tabular-nums">{timeStr}</span>
          </div>
          <div data-testid="verify-countdown" className="mt-6 font-heading font-bold text-8xl tabular-nums">{countdown}</div>
          <p className="text-sm opacity-80">Show this to your server — closes automatically</p>

          <button data-testid="close-verify-btn" onClick={stopVerify}
            className="mt-8 px-6 py-2 bg-white text-[#17324D] font-semibold border border-[#17324D]/15 rounded-xl">
            Done
          </button>
        </div>
      )}
    </div>
  );
}
